import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from 'src/app/auth.service';
import {
  TimeEntry,
  TimesheetService,
} from 'src/app/services/timesheet.service';

declare var bootstrap: any;

interface WeekDayGroup {
  date: Date;
  dateKey: string;
  dayLabel: string;
  dateLabel: string;
  entries: TimeEntry[];
  dayTotal: number;
  isToday: boolean;
  gapNotes: GapNote[];
  rowSpan: number;
}

interface GapNote {
  afterIndex: number;
  minutes: number;
  isVoided: boolean;
  label: string;
}

@Component({
  selector: 'app-timesheet',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './timesheet.component.html',
  styleUrls: ['./timesheet.component.scss'],
})
export class TimesheetComponent implements OnInit {
  @ViewChild('manualEntryModal', { static: false })
  manualEntryModalRef?: ElementRef<HTMLDivElement>;

  private readonly auth = inject(AuthService);
  private readonly timesheetService = inject(TimesheetService);
  private readonly fb = inject(FormBuilder);

  currentUser: any = null;
  employeeId = '';
  employeeName = '';

  weekStart = this.getWeekStart(new Date());
  weekLabel = '';

  groups: WeekDayGroup[] = [];
  weekTotal = 0;
  activeEntry: TimeEntry | null = null;

  isLoading = false;
  isClockActionLoading = false;
  manualSaving = false;
  errorMessage = '';

  selectedManualDateKey = '';
  selectedManualDateLabel = '';

  readonly manualEntryForm = this.fb.nonNullable.group({
    timeIn: ['', Validators.required],
    timeOut: ['', Validators.required],
    note: [''],
  });

  private manualModalInstance: any;

  async ngOnInit(): Promise<void> {
    this.currentUser = this.auth.getUser();

    if (!this.currentUser?.id) {
      this.errorMessage = 'You need to be logged in to use timesheet.';
      return;
    }

    this.employeeId = this.currentUser.id;
    this.employeeName =
      this.auth.getUserName() || this.currentUser.Name || 'Employee';

    this.refreshWeekLabel();
    await this.loadTimesheetData();
  }

  get isClockedIn(): boolean {
    return !!this.activeEntry;
  }

  async goToPreviousWeek(): Promise<void> {
    const nextStart = new Date(this.weekStart);
    nextStart.setDate(nextStart.getDate() - 7);
    this.weekStart = nextStart;
    this.refreshWeekLabel();
    await this.loadWeekEntriesOnly();
  }

  async goToNextWeek(): Promise<void> {
    const nextStart = new Date(this.weekStart);
    nextStart.setDate(nextStart.getDate() + 7);
    this.weekStart = nextStart;
    this.refreshWeekLabel();
    await this.loadWeekEntriesOnly();
  }

  async onClockAction(): Promise<void> {
    if (this.isClockActionLoading || !this.employeeId) {
      return;
    }

    this.errorMessage = '';
    this.isClockActionLoading = true;

    try {
      if (this.activeEntry) {
        await this.timesheetService.clockOut(this.activeEntry.id);
      } else {
        await this.timesheetService.clockIn(this.employeeId, this.employeeName);
      }

      await this.loadTimesheetData();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to save timesheet action.';
    } finally {
      this.isClockActionLoading = false;
    }
  }

  openManualEntryModal(group: WeekDayGroup): void {
    this.selectedManualDateKey = group.dateKey;
    this.selectedManualDateLabel = `${group.dayLabel}, ${group.dateLabel}`;
    this.manualEntryForm.reset({ timeIn: '', timeOut: '', note: '' });

    if (!this.manualModalInstance && this.manualEntryModalRef?.nativeElement) {
      this.manualModalInstance = new bootstrap.Modal(
        this.manualEntryModalRef.nativeElement,
      );
    }

    this.manualModalInstance?.show();
  }

  async submitManualEntry(): Promise<void> {
    if (this.manualSaving || this.manualEntryForm.invalid) {
      this.manualEntryForm.markAllAsTouched();
      return;
    }

    if (!this.selectedManualDateKey) {
      this.errorMessage = 'Please choose a date before adding a manual entry.';
      return;
    }

    const { timeIn, timeOut, note } = this.manualEntryForm.getRawValue();
    const start = this.buildDateTime(this.selectedManualDateKey, timeIn);
    const end = this.buildDateTime(this.selectedManualDateKey, timeOut);

    if (!start || !end || end <= start) {
      this.errorMessage = 'Time Out must be after Time In.';
      return;
    }

    this.errorMessage = '';
    this.manualSaving = true;

    try {
      await this.timesheetService.addManualEntry(
        this.employeeId,
        this.employeeName,
        this.selectedManualDateKey,
        start,
        end,
        note || '',
      );

      this.manualModalInstance?.hide();
      await this.loadWeekEntriesOnly();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to add manual entry.';
    } finally {
      this.manualSaving = false;
    }
  }

  formatTime(value: any): string {
    const date = this.toDate(value);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  trackByDate(_index: number, group: WeekDayGroup): string {
    return group.dateKey;
  }

  getGapNote(group: WeekDayGroup, afterIndex: number): GapNote | null {
    return (
      group.gapNotes.find((note) => note.afterIndex === afterIndex) || null
    );
  }

  private async loadTimesheetData(): Promise<void> {
    if (!this.employeeId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const [activeEntry, entries] = await Promise.all([
        this.timesheetService.getActiveEntry(this.employeeId),
        this.timesheetService.getEntriesForWeek(
          this.employeeId,
          this.weekStart,
        ),
      ]);

      this.activeEntry = activeEntry;
      this.groups = this.buildWeekGroups(entries);
      this.weekTotal = this.groups.reduce((sum, g) => sum + g.dayTotal, 0);
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load timesheet.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadWeekEntriesOnly(): Promise<void> {
    if (!this.employeeId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const entries = await this.timesheetService.getEntriesForWeek(
        this.employeeId,
        this.weekStart,
      );

      this.groups = this.buildWeekGroups(entries);
      this.weekTotal = this.groups.reduce((sum, g) => sum + g.dayTotal, 0);
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load week entries.';
    } finally {
      this.isLoading = false;
    }
  }

  private buildWeekGroups(entries: TimeEntry[]): WeekDayGroup[] {
    const byDate = new Map<string, TimeEntry[]>();
    for (const entry of entries) {
      if (!byDate.has(entry.date)) {
        byDate.set(entry.date, []);
      }
      byDate.get(entry.date)!.push(entry);
    }

    const groups: WeekDayGroup[] = [];
    const todayKey = this.toDateKey(new Date());

    for (let i = 0; i < 7; i++) {
      const date = new Date(this.weekStart);
      date.setDate(this.weekStart.getDate() + i);

      const dateKey = this.toDateKey(date);
      const sortedDayEntries = (byDate.get(dateKey) || [])
        .slice()
        .sort(
          (a, b) =>
            this.toDate(a.clockIn).getTime() - this.toDate(b.clockIn).getTime(),
        );
      const gapNotes = this.buildGapNotes(sortedDayEntries);
      const dayTotal =
        this.timesheetService.calculateDayTotal(sortedDayEntries);

      groups.push({
        date,
        dateKey,
        dayLabel: date.toLocaleDateString([], { weekday: 'short' }),
        dateLabel: date.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        entries: sortedDayEntries,
        dayTotal,
        isToday: dateKey === todayKey,
        gapNotes,
        rowSpan: sortedDayEntries.length + gapNotes.length,
      });
    }

    return groups;
  }

  private refreshWeekLabel(): void {
    const weekEnd = new Date(this.weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const startMonth = this.weekStart.toLocaleDateString([], {
      month: 'short',
    });
    const startDay = this.weekStart.getDate();
    const endMonth = weekEnd.toLocaleDateString([], { month: 'short' });
    const endDay = weekEnd.getDate();
    const year = weekEnd.getFullYear();

    this.weekLabel = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  }

  private getWeekStart(date: Date): Date {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    const day = value.getDay();
    value.setDate(value.getDate() - day);
    return value;
  }

  private toDate(value: any): Date {
    if (value?.toDate) {
      return value.toDate();
    }
    return value instanceof Date ? value : new Date(value);
  }

  private buildGapNotes(entries: TimeEntry[]): GapNote[] {
    const notes: GapNote[] = [];

    for (let i = 0; i < entries.length - 1; i++) {
      const current = entries[i];
      const next = entries[i + 1];

      if (!current.clockOut) {
        continue;
      }

      const currentOut = this.toDate(current.clockOut);
      const nextIn = this.toDate(next.clockIn);
      const gapMinutes = Math.round(
        (nextIn.getTime() - currentOut.getTime()) / (1000 * 60),
      );

      if (gapMinutes > 0 && gapMinutes <= 15) {
        notes.push({
          afterIndex: i,
          minutes: gapMinutes,
          isVoided: true,
          label: `(${gapMinutes} min break - not deducted)`,
        });
      } else if (gapMinutes > 15) {
        notes.push({
          afterIndex: i,
          minutes: gapMinutes,
          isVoided: false,
          label: `(${gapMinutes} min lunch break)`,
        });
      }
    }

    return notes;
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private buildDateTime(dateKey: string, timeValue: string): Date | null {
    if (!timeValue) {
      return null;
    }

    const [hoursRaw, minutesRaw] = timeValue.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    const [yearRaw, monthRaw, dayRaw] = dateKey.split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);

    if (
      Number.isNaN(year) ||
      Number.isNaN(month) ||
      Number.isNaN(day) ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }
}
